import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Clock, Activity, TrendingUp, TrendingDown } from 'lucide-react';
import { useSocketContext } from '../../App';
import { format } from 'date-fns';

const pageTitles = {
  '/': 'DASHBOARD',
  '/zones': 'TRADER ZONES',
  '/items': 'ITEM BROWSER',
  '/analytics': 'ANALYTICS',
};

function getPageTitle(pathname) {
  if (pathname.startsWith('/zones/')) {
    return `ZONE: ${decodeURIComponent(pathname.split('/zones/')[1]).toUpperCase()}`;
  }
  if (pathname.startsWith('/items/')) {
    return `ITEM: ${decodeURIComponent(pathname.split('/items/')[1]).toUpperCase()}`;
  }
  return pageTitles[pathname] || 'TERMINAL';
}

export default function Header() {
  const location = useLocation();
  const socket = useSocketContext();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [recentTransactions, setRecentTransactions] = useState([]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (socket?.lastTransaction) {
      setRecentTransactions((prev) =>
        [socket.lastTransaction, ...prev].slice(0, 20)
      );
    }
  }, [socket?.lastTransaction]);

  const title = getPageTitle(location.pathname);

  const tickerItems = useMemo(() => {
    if (recentTransactions.length === 0) {
      return [
        { text: 'Awaiting live transaction data...', type: 'info' },
      ];
    }
    return recentTransactions.map((tx, i) => ({
      text: `${tx.classname || tx.item || 'Unknown'} ${tx.type === 'SELL' ? '▲' : '▼'} ${tx.quantity || 1}x @ ₽${tx.price || 0}`,
      type: tx.type === 'SELL' ? 'up' : 'down',
      zone: tx.zone || '',
      key: `${tx.timestamp || i}-${i}`,
    }));
  }, [recentTransactions]);

  return (
    <header className="h-14 bg-terminal-surface border-b border-terminal-border flex items-center shrink-0">
      {/* Page Title */}
      <div className="flex items-center gap-3 px-6 min-w-[200px] border-r border-terminal-border h-full">
        <Activity className="w-4 h-4 text-accent-green" />
        <h2 className="text-sm font-bold text-text-primary tracking-wider font-mono">
          {title}
        </h2>
      </div>

      {/* Ticker Tape */}
      <div className="flex-1 overflow-hidden h-full flex items-center px-4">
        <div className="ticker-wrap w-full">
          <div className="ticker-content">
            {tickerItems.map((item, idx) => (
              <span
                key={item.key || idx}
                className={`inline-flex items-center gap-1 mx-4 text-xs font-mono ${
                  item.type === 'up'
                    ? 'text-accent-green'
                    : item.type === 'down'
                    ? 'text-accent-red'
                    : 'text-text-muted'
                }`}
              >
                {item.type === 'up' && <TrendingUp className="w-3 h-3" />}
                {item.type === 'down' && <TrendingDown className="w-3 h-3" />}
                {item.text}
                {item.zone && (
                  <span className="text-text-muted ml-1">[{item.zone}]</span>
                )}
              </span>
            ))}
            {/* Duplicate for seamless loop */}
            {tickerItems.map((item, idx) => (
              <span
                key={`dup-${item.key || idx}`}
                className={`inline-flex items-center gap-1 mx-4 text-xs font-mono ${
                  item.type === 'up'
                    ? 'text-accent-green'
                    : item.type === 'down'
                    ? 'text-accent-red'
                    : 'text-text-muted'
                }`}
              >
                {item.type === 'up' && <TrendingUp className="w-3 h-3" />}
                {item.type === 'down' && <TrendingDown className="w-3 h-3" />}
                {item.text}
                {item.zone && (
                  <span className="text-text-muted ml-1">[{item.zone}]</span>
                )}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Clock + Stats */}
      <div className="flex items-center gap-4 px-6 border-l border-terminal-border h-full">
        <div className="flex items-center gap-4 text-xs font-mono">
          <div className="flex items-center gap-1.5 text-text-secondary">
            <span className="text-text-muted">TXN:</span>
            <span className="text-accent-green">{recentTransactions.length}</span>
          </div>
          <div className="flex items-center gap-1.5 text-text-secondary">
            <span className="text-text-muted">ZONES:</span>
            <span className="text-accent-blue">7</span>
          </div>
        </div>
        <div className="w-px h-6 bg-terminal-border" />
        <div className="flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5 text-text-muted" />
          <span className="text-xs font-mono text-text-secondary">
            {format(currentTime, 'HH:mm:ss')}
          </span>
        </div>
      </div>
    </header>
  );
}
